import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  TableContainer,
  Chip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Switch,
  FormControlLabel,
  InputAdornment,
  IconButton,
  CircularProgress, // Import CircularProgress
} from "@mui/material";
import { apiLogsExportReport, jobProviderList, jobProviderUpdate } from "../Dashboard/services";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Close as CloseIcon } from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { userManagement } from "../../config/images";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  addAiModel,
  getAiModel,
  updateAiModel,
  deleteAiModel,
} from "./services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { downloadBinaryFile } from "../../utils/utils";

const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  const parts = dateString.split("-");
  if (parts.length === 3) {
    // Assuming DD-MM-YYYY format
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateString;
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "8px",
};

const ApiScraperSettings = () => {
  const [jobSources, setJobSources] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [llmPage, setllmPage] = useState(1);
  const [llmPageSize, setllmPageSize] = useState(5);
  const [llmTotalCount, setllmTotalCount] = useState(0);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [apiStatusFilter, setApiStatusFilter] = useState("All");

  const [isAddLLMModalOpen, setIsAddLLMModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditLLMModalOpen, setIsEditLLMModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});

  const [selectedLLM, setSelectedLLM] = useState("");
  const [llmFormData, setLlmFormData] = useState({
    llmName: "",
    customLlmName: "",
    keys: "",
    status: "",
    purchaseDate: "",
  });
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const [llmData, setLlmData] = useState([]);
  const [llmLoading, setLlmLoading] = useState(false);

  // New state variables for button loaders
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [selectedLlmData, setSelectedLlmData] = useState(null);

  const [editFormData, setEditFormData] = useState({
    llmName: "",
    keys: "",
    status: "",
    purchaseDate: "",
  });

  useEffect(() => {
    if (isEditLLMModalOpen && selectedLlmData) {
      setEditFormData({
        llmName: selectedLlmData.name,
        keys: selectedLlmData.api_key,
        status: selectedLlmData.is_active ? "Active" : "Inactive",
        purchaseDate: formatDateForInput(selectedLlmData.purchase_date),
      });
    }
  }, [isEditLLMModalOpen, selectedLlmData]);

  const handleEditFormDataChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateLLM = async () => {
    setEditLoading(true);
    try {
      const payload = {
        name: editFormData.llmName,
        api_key: editFormData.keys,
        purchase_date: formatDateToDDMMYYYY(editFormData.purchaseDate),
        is_active: editFormData.status === "Active" ? true : false,
      };
      const res = await updateAiModel(selectedLlmData.id, payload);

      toast.success(res.data.message, { toastId: "edit-llm-success" });
      handleEditLLMClose();
      setSelectedLlmData(null);
      fetchLlmData();
    } catch (error) {
      console.error("Error updating LLM model:", error);
      toast.error(
        error.response?.data?.message,
        { toastId: "edit-llm-error" }
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteLLM = async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteAiModel(selectedLlmData.id);

      toast.success(res.data.message, { toastId: "delete-llm-success" });
      handleDeleteClose();
      fetchLlmData();
    } catch (error) {
      console.error("Error deleting LLM model:", error);
      toast.error(
        error.response?.data?.message,
        { toastId: "delete-llm-error" }
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLLMSubmit = async () => {
    if (!llmFormData.llmName || !llmFormData.keys) {
      toast.error("Please fill in all required fields.", {
        toastId: "add-llm-require-error",
      });
      return;
    }
    setAddLoading(true);
    try {
      const payload = {
        name:
          selectedLLM === "Other"
            ? llmFormData.customLlmName
            : llmFormData.llmName,
        api_key: llmFormData.keys,
        purchase_date: formatDateToDDMMYYYY(llmFormData.purchaseDate),
        is_active: llmFormData.status === "Active" ? true : false,
      };
      const res = await addAiModel(payload);

      toast.success(res.data.message, { toastId: "add-llm-success" });
      handleAddLLMClose();
      setSelectedLLM("");
      setLlmFormData({
        llmName: "",
        customLlmName: "",
        keys: "",
        status: "",
        purchaseDate: "",
      });
      fetchLlmData();
    } catch (error) {
      if (selectedLLM === "Other" && error.response?.status === 400) {
        handleAddLLMClose();
        setIsSuccessModalOpen(true);
      } else {
        console.error("Error adding LLM model:", error);
        toast.error(
          error.response?.data?.message,
          { toastId: "add-llm-error" }
        );
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleToggleVisibility = (name) => {
    setVisibleKeys((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };
  const handleLLMPageChange = (event, value) => {
    setllmPage(value);
  };

  const handleApiStatusFilterChange = (event) => {
    setApiStatusFilter(event.target.value);
    setPage(1);
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleOpenConfigureModal = () => setIsConfigureModalOpen(true);
  const handleCloseConfigureModal = () => setIsConfigureModalOpen(false);

  const handleAddLLMOpen = () => setIsAddLLMModalOpen(true);
  const handleAddLLMClose = () => {
    setIsAddLLMModalOpen(false);
    setSelectedLLM("");
    setLlmFormData({
      llmName: "",
      customLlmName: "",
      keys: "",
      status: "",
      purchaseDate: "",
    });
  };

  const handleSuccessModalClose = () => setIsSuccessModalOpen(false);

  const handleLLMChange = (event) => {
    const value = event.target.value;
    setSelectedLLM(value);
    setLlmFormData((prev) => ({
      ...prev,
      llmName: value,
    }));
  };

  const handleFormDataChange = (field, value) => {
    setLlmFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleViewOpen = (llm) => {
    setSelectedLlmData(llm);
    setIsViewModalOpen(true);
  };
  const handleViewClose = () => setIsViewModalOpen(false);

  const handleEditLLMOpen = (llm) => {
    setSelectedLlmData(llm);
    setIsEditLLMModalOpen(true);
  };
  const handleEditLLMClose = () => {
    setSelectedLlmData(null);
    setIsEditLLMModalOpen(false);
  };

  const handleDeleteOpen = (llm) => {
    setSelectedLlmData(llm);
    setIsDeleteModalOpen(true);
  };
  const handleDeleteClose = () => {
    setSelectedLlmData(null);
    setIsDeleteModalOpen(false);
  };

  const fetchLlmData = async () => {
    const params = {
      llmPage,
      llmPageSize,
    };
    try {
      setLlmLoading(true);
      const response = await getAiModel(params);
      setLlmData(response.data.payload.results || []);
      setllmTotalCount(response.data.payload.count);
    } catch (error) {
      console.error("Error fetching LLM data:", error);
      toast.error("Failed to fetch LLM data", { toastId: "fetch-llm-error" });
    } finally {
      setLlmLoading(false);
    }
  };
  const handleExportReport = async () => {
      try {
        const response = await apiLogsExportReport();
        downloadBinaryFile(response, "Api Logs Report.xlsx");
      } catch (error) {
        console.error("Error fetching API data:", error);
        toast.error("Error fetching report export data");
      }
    };

  useEffect(() => {
    fetchJobSources();
   
  }, [page, pageSize, apiStatusFilter]);

  useEffect(() => {
    fetchLlmData();
  }, [llmPage, llmPageSize]);

  const fetchJobSources = async () => {
    try {
      const params = {
        page,
        pageSize,
      };
      if (apiStatusFilter !== "All") {
        params.api_status = apiStatusFilter;
      }
      const response = await jobProviderList(
        params.page,
        params.pageSize,
        params.api_status
      );
      setJobSources(response.data.results.payload);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error("Error fetching job sources:", error);
    }
  };

  const handleActionClick = async (id, isActive) => {
    try {
      const newIsActive = !isActive;
      await jobProviderUpdate({
        id: id,
        is_active: newIsActive,
      });
      setJobSources((prevData) =>
        prevData.map((item) =>
          item.id === id
            ? {
                ...item,
                is_active: newIsActive,
                status: newIsActive ? "Active" : "Inactive",
              }
            : item
        )
      );
    } catch (error) {
      console.error("Error updating job provider status:", error);
    }
  };

  const ConfigureIntegrationModal = ({ open, handleClose }) => {
    const [formValues, setFormValues] = useState({
      integrationName: "",
      provider: "",
      status: false,
      apiKey: "",
      clientId: "",
      clientSecret: "",
      rateLimit: "",
      syncFrequency: "",
      errorThreshold: "",
      notificationEmail: "",
      autoPause: true,
      retryAttempts: 3,
      retryDelay: 5,
      backoffMultiplier: 2,
    });

    const handleChange = (e) => {
      const { name, value, checked, type } = e.target;
      setFormValues((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    };

    const handleStatusChange = (e) => {
      setFormValues((prev) => ({
        ...prev,
        status: e.target.checked,
      }));
    };

    const handleSave = () => {
      console.log("Saving form data:", formValues);
      handleClose();
    };

    const handleIncrement = (field) => {
      setFormValues((prev) => ({
        ...prev,
        [field]: prev[field] + 1,
      }));
    };

    const handleDecrement = (field) => {
      if (formValues[field] > 0) {
        setFormValues((prev) => ({
          ...prev,
          [field]: prev[field] - 1,
        }));
      }
    };

    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "20px",
            mt: 3,
          }}
        >
          Configure Integration
          <br />
          New Integration
        </DialogTitle>
        <DialogContent dividers sx={{ padding: "25px 32px" }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Integration Details
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Integration Name
            </Typography>
            <TextField
              placeholder="E.g. linkedin jobs API"
              size="small"
              name="integrationName"
              value={formValues.integrationName}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Provider
            </Typography>
            <TextField
              placeholder="Provider name"
              size="small"
              name="provider"
              value={formValues.provider}
              onChange={handleChange}
            />
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formValues.status}
                onChange={handleStatusChange}
                color="primary"
              />
            }
            label="Inactive"
            labelPlacement="end"
            sx={{ mb: 4 }}
          />

          <Typography variant="h6" fontWeight="bold" mb={2}>
            Authentication Settings
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              API Key / Credentials
            </Typography>
            <TextField
              placeholder="Enter API key or token"
              size="small"
              name="apiKey"
              value={formValues.apiKey}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <VisibilityOutlinedIcon
                      sx={{ color: "#A0A0A0", cursor: "pointer", mr: 1 }}
                    />
                    <RefreshIcon sx={{ color: "#A0A0A0", cursor: "pointer" }} />
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Client ID
            </Typography>
            <TextField
              placeholder="Enter client ID"
              size="small"
              name="clientId"
              value={formValues.clientId}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 4 }}>
            <Typography variant="body1" mb={1}>
              Client Secret
            </Typography>
            <TextField
              placeholder="Enter client secret"
              size="small"
              name="clientSecret"
              value={formValues.clientSecret}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <VisibilityOutlinedIcon
                      sx={{ color: "#A0A0A0", cursor: "pointer", mr: 1 }}
                    />
                    <RefreshIcon sx={{ color: "#A0A0A0", cursor: "pointer" }} />
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>

          <Typography variant="h6" fontWeight="bold" mb={2}>
            Sync & Rate Settings
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Rate Limit (requests/minute)
            </Typography>
            <TextField
              placeholder="Enter rate limit"
              size="small"
              name="rateLimit"
              value={formValues.rateLimit}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Sync Frequency
            </Typography>
            <Select
              size="small"
              displayEmpty
              name="syncFrequency"
              value={formValues.syncFrequency}
              onChange={handleChange}
            >
              <MenuItem value="" disabled>
                Select Sync Frequency
              </MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="hourly">Hourly</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Last Sync:
            </Typography>
            <TextField
              placeholder="Never Synced"
              size="small"
              name="rateLimit"
              value={formValues.rateLimit}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 4 }}>
            <Typography variant="body1" mb={1}>
              Error Threshold(%)
            </Typography>
            <TextField
              placeholder="Error Threshold(%)"
              size="small"
              name="errorThreshold"
              value={formValues.errorThreshold}
              onChange={handleChange}
            />
          </FormControl>

          <Typography variant="h6" fontWeight="bold" mb={2}>
            Monitoring & Alerts
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Notification Email
            </Typography>
            <TextField
              placeholder="admin@example.com"
              size="small"
              name="notificationEmail"
              value={formValues.notificationEmail}
              onChange={handleChange}
            />
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formValues.autoPause}
                onChange={(e) => handleChange(e)}
                name="autoPause"
                color="primary"
              />
            }
            label="Enable auto-pause on high error rate"
            labelPlacement="end"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Retry Attempts
            </Typography>
            <TextField
              size="small"
              type="number"
              name="retryAttempts"
              value={formValues.retryAttempts}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Retry Delay (seconds)
            </Typography>
            <TextField
              size="small"
              type="number"
              name="retryDelay"
              value={formValues.retryDelay}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1}>
              Backoff Multiplier
            </Typography>
            <TextField
              size="small"
              type="number"
              name="backoffMultiplier"
              value={formValues.backoffMultiplier}
              onChange={handleChange}
            />
          </FormControl>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ flex: 1, textTransform: "capitalize" }}
          >
            Test Connection
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{ width: "100%", gap: 2 }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ flex: 1, textTransform: "capitalize" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                flex: 1,
                backgroundColor: "#1570EF",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "#115bbd",
                },
                textTransform: "capitalize",
              }}
            >
              Save Changes
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  };
  return (
    <Box
      sx={{
        backgroundColor: "#eaf3fc",
        minHeight: "94vh",
        padding: { xs: "14px", md: "36px" },
      }}
    >
      <Box
        sx={{
          background: "#ffffff",
          padding: "30px",
          border: "1px solid #1570EF",
          borderRadius: "8px",
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          flexWrap="wrap"
          alignItems={{ xs: "flex-start", sm: "center" }}
          mb={3}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: "24px", md: "36px", sm: "32px" },
              fontWeight: 600,
            }}
          >
            API & Scraper Settings
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <button
              variant="contained"
              className="export-report-btn"
              style={{
                fontWeight: "600",
                padding: "10px 20px",
              }}
            >
              Refresh Status
            </button>
            <button className="api-integration-btn" onClick={handleOpenModal}>
              Add Integration
            </button>
          </Box>
        </Box>
        <Typography variant="h6" mb={{ xs: 2, sm: 0 }} sx={{ mb: 2 }}>
          Job Source Integrations
        </Typography>
        <Box sx={{ mb: 3 }}>
          <TableContainer
            sx={{ overflowX: "auto", border: "1px solid #e0e0e0" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>NAME</b>
                  </TableCell>
                  <TableCell>
                    <b>PROVIDER</b>
                  </TableCell>
                  <TableCell>
                    <b>STATUS</b>
                  </TableCell>
                  <TableCell>
                    <b>RATE LIMIT</b>
                  </TableCell>
                  <TableCell>
                    <b>LAST SYNC</b>
                  </TableCell>
                  <TableCell>
                    <b>ERROR RATE</b>
                  </TableCell>
                  <TableCell>
                    <b>ACTIONS</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobSources.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.provider}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        sx={{
                          background:
                            row.status === "Active"
                              ? "#E3FFEC !important"
                              : "#FEE2E2",
                          color:
                            row.status === "Active"
                              ? "#5F9A72 !important"
                              : "#C33D1B",
                          fontSize: "12px",
                          borderRadius: "6px",
                          padding: "15px 4px",
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{row.rate_limit}</TableCell>
                    <TableCell>{row.last_sync}</TableCell>
                    <TableCell
                      sx={{
                        color: row.status == "Active" ? "#22C55E" : "#EF4444",
                      }}
                    >
                      {row.error_rate}
                    </TableCell>
                    <TableCell sx={{ display: "flex", gap: "8px" }}>
                      <Button
                        size="small"
                        sx={{
                          border: "1px solid #333333 !important",
                          color: "#333333 !important",
                          textTransform: "capitalize !important",
                          padding: "4px 10px !important",
                        }}
                        onClick={handleOpenConfigureModal}
                      >
                        Configure
                      </Button>
                      <button
                        onClick={() => handleActionClick(row.id, row.is_active)}
                        style={{
                          background: row.is_active ? "orange" : "#22C55E",
                          color: "#fff",
                          border: "none",
                          padding: "4px 8px",
                          borderRadius: "6px",
                        }}
                      >
                        {row.is_active ? "Pause" : "Active"}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2">
            Showing {(page - 1) * pageSize + 1} To{" "}
            {Math.min(page * pageSize, totalCount)} Of {totalCount} Results
          </Typography>
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
          />
        </Box>

        <Box
          sx={{
            mt: 3,
            mb: 2,
            display: "flex",
            justifyContent: { xs: "center", md: "space-between" },
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" mb={{ xs: 2, sm: 0 }}>
            LLM API & Credentials
          </Typography>
          <button className="api-integration-btn" onClick={handleAddLLMOpen}>
            Add LLM API & Credentials
          </button>
        </Box>
    

        <Box sx={{ mb: 3 }}>
          <TableContainer
            sx={{ overflowX: "auto", border: "1px solid #e0e0e0" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>LLM Name</b>
                  </TableCell>
                  <TableCell>
                    <b>KEY Name</b>
                  </TableCell>
                  <TableCell>
                    <b>KEYS</b>
                  </TableCell>
                  <TableCell>
                    <b>STATUS</b>
                  </TableCell>
                  <TableCell>
                    <b>PURCHASED DATE</b>
                  </TableCell>
                  <TableCell>
                    <b>ACTIONS</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {llmLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : llmData.length > 0 ? (
                  llmData.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.key_name || "N/A"}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {visibleKeys[row.id]
                            ? row.api_key
                            : "****************"}
                          <IconButton
                            onClick={() => handleToggleVisibility(row.id)}
                          >
                            {visibleKeys[row.id] ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.is_active ? "Active" : "Inactive"}
                          sx={{
                            background: row.is_active
                              ? "#E3FFEC !important"
                              : "#FEE2E2",
                            color: row.is_active
                              ? "#5F9A72 !important"
                              : "#C33D1B",
                            fontSize: "12px",
                            borderRadius: "6px",
                            padding: "15px 4px",
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{row.purchase_date}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: "15px" }}>
                          <img
                            src={userManagement.viewUser}
                            alt=""
                            style={{ objectFit: "contain", cursor: "pointer" }}
                            onClick={() => handleViewOpen(row)}
                          />
                          <img
                            src={userManagement.editUser}
                            alt=""
                            style={{ objectFit: "contain", cursor: "pointer" }}
                            onClick={() => handleEditLLMOpen(row)}
                          />
                          <img
                            src={userManagement.deleteUser}
                            alt=""
                            style={{ objectFit: "contain", cursor: "pointer" }}
                            onClick={() => handleDeleteOpen(row)}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No LLM data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2">
            Showing {(llmPage - 1) * llmPageSize + 1} To{" "}
            {Math.min(llmPage * llmPageSize, llmTotalCount)} Of {llmTotalCount} Results
          </Typography>
          <Pagination
            count={Math.ceil(llmTotalCount / llmPageSize)}
            page={llmPage}
            onChange={handleLLMPageChange}
            shape="rounded"
          />
        </Box>

        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent={{ xs: "center", md: "space-between" }}
          alignItems={{ xs: "center", sm: "center" }}
          mb={{ xs: 2, md: 0 }}
        >
          <Typography variant="h6" mb={{ xs: 2, sm: 0 }} sx={{ mt: 3, mb: 2 }}>
            API Logs
          </Typography>
          <Box>
            <TextField
              select
              size="small"
              sx={{ minWidth: 120 }}
              value={apiStatusFilter}
              onChange={handleApiStatusFilterChange}
            >
              <MenuItem value="All">All Status</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </TextField>
            <button
              variant="contained"
              className="export-report-btn"
              onClick={handleExportReport}
              style={{
                fontWeight: "600",
                padding: "8px 20px",
                marginLeft: "12px",
              }}
            >
              Export Logs
            </button>
          </Box>
        </Box>
        <Box sx={{ mb: 3 }}>
          <TableContainer
            sx={{ overflowX: "auto", border: "1px solid #e0e0e0" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>INTEGRATION</b>
                  </TableCell>
                  <TableCell>
                    <b>TIMESTAMP</b>
                  </TableCell>
                  <TableCell>
                    <b>STATUS</b>
                  </TableCell>
                  <TableCell>
                    <b>ENDPOINT</b>
                  </TableCell>
                  <TableCell>
                    <b>RESPONSE TIME</b>
                  </TableCell>
                  <TableCell sx={{ width: "200px" }}>
                    <b>ERROR</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobSources?.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.last_sync}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.api_status}
                        sx={{
                          background:
                            row.api_status === "success"
                              ? "#E3FFEC !important"
                              : row.api_status === "error"
                              ? "#FEE2E2"
                              : "",
                          color:
                            row.api_status === "success"
                              ? "#5F9A72 !important"
                              : row.api_status === "error"
                              ? "#C33D1B"
                              : "",
                          fontSize: "12px",
                          borderRadius: "6px",
                          padding: "15px 5px",
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{row.endpoint}</TableCell>
                    <TableCell
                      sx={{
                        color:
                          row.api_status == "success" ? "#22C55E" : "#EF4444",
                      }}
                    >
                      {row.response_time}
                    </TableCell>

                    <TableCell>{row.error_message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2">
            Showing {(page - 1) * pageSize + 1} To{" "}
            {Math.min(page * pageSize, totalCount)} Of {totalCount} Results
          </Typography>
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
          />
        </Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ mt: 3, mb: 4, textAlign: { xs: "center", md: "left" } }}
        >
          Rate Limits & Settings
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max Requests Per Minute"
              defaultValue={100}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Concurrent Scraper Threads"
              defaultValue={5}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Max Retry Attempts" defaultValue={3} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Retry Delay (seconds)"
              defaultValue={5}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Error Rate Threshold (%)"
              defaultValue={5}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Backoff Multiplier" defaultValue={2} />
          </Grid>
          <Grid item xs={12} md={12}>
            <TextField
              fullWidth
              label="Notification Email"
              defaultValue="admin@example.com"
            />
          </Grid>
        </Grid>

        <Box
          display="flex"
          sx={{ justifyContent: { xs: "center", md: "flex-end" } }}
          mt={3}
        >
          <button
            variant="contained"
            className="export-report-btn"
            style={{ fontWeight: "600", padding: "10px 20px" }}
          >
            Cancel
          </button>
          <button
            variant="contained"
            className="api-integration-btn"
            style={{
              fontWeight: "600",
              padding: "10px 20px",
              marginLeft: "12px",
            }}
          >
            Save Changes
          </button>
        </Box>
      </Box>

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="add-integration-modal-title"
        aria-describedby="add-integration-modal-description"
      >
        <DialogTitle
          id="add-integration-modal-title"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "20px",
            mt: 3,
          }}
        >
          Add New Integration
        </DialogTitle>
        <DialogContent>
          <Typography
            id="add-integration-modal-description"
            sx={{ mb: 3, fontSize: "15px", color: "#333333" }}
            textAlign="center"
          >
            Configure a new job source integration for the system
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1} fontWeight="bold">
              Integration Name
            </Typography>
            <TextField placeholder="E.g. linkedin jobs API" size="small" />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1} fontWeight="bold">
              Provider
            </Typography>
            <TextField placeholder="Provider name" size="small" />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1} fontWeight="bold">
              API Key / Credentials
            </Typography>
            <TextField
              placeholder="Enter API key or token"
              size="small"
              InputProps={{
                endAdornment: (
                  <VisibilityOutlinedIcon
                    sx={{ color: "#A0A0A0", cursor: "pointer" }}
                  />
                ),
              }}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1} fontWeight="bold">
              Rate Limit(req/min)
            </Typography>
            <TextField placeholder="Enter rate limit" size="small" />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1} fontWeight="bold">
              Sync Frequency
            </Typography>
            <Select size="small" displayEmpty defaultValue="">
              <MenuItem value="" disabled>
                Select Sync Frequency
              </MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="hourly">Hourly</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body1" mb={1} fontWeight="bold">
              Error Threshold(%)
            </Typography>
            <TextField placeholder="Error Threshold(%)" size="small" />
          </FormControl>
          <Typography variant="body1" mb={1} fontWeight="bold">
            Status
          </Typography>
          <FormControlLabel
            control={<Switch defaultChecked />}
            label="InActive"
            labelPlacement="end"
          />
        </DialogContent>
        <DialogActions>
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{ width: "100%", gap: 2, p: 2 }}
          >
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                flex: 1,
                color: "#1570EF",
                borderColor: "#1570EF",
                textTransform: "capitalize",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                flex: 1,
                backgroundColor: "#1570EF",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "#115bbd",
                },
                textTransform: "capitalize",
              }}
            >
              Save Integration
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <ConfigureIntegrationModal
        open={isConfigureModalOpen}
        handleClose={handleCloseConfigureModal}
      />

      <Dialog
        open={isAddLLMModalOpen}
        onClose={handleAddLLMClose}
        sx={{
          "& .MuiDialog-paper": {
            width: "400px",
            maxWidth: "400px",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "600", fontSize: "24px" }}
        >
          Add LLM API & Credentials
        </DialogTitle>
        <DialogContent
          sx={{
            "&::-webkit-scrollbar": {
              display: "none",
            },
            "-ms-overflow-style": "none",
            "scrollbar-width": "none",
            overflow: "auto",
          }}
        >
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            LLM Name
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={selectedLLM}
            onChange={handleLLMChange}
            sx={{ mt: 0 }}
          >
            <MenuItem value="" disabled>
              Select LLM Name
            </MenuItem>
            <MenuItem value="Gemini">Gemini</MenuItem>
            <MenuItem value="ChatGPT">ChatGPT</MenuItem>
            <MenuItem value="Claude">Claude</MenuItem>
            <MenuItem value="Llama">Llama</MenuItem>
            <MenuItem value="Career Expert">Career Expert</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>

          {selectedLLM === "Other" && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5 }}>
                LLM Name
              </Typography>
              <TextField
                fullWidth
                placeholder="LLM Name"
                value={llmFormData.customLlmName}
                onChange={(e) =>
                  handleFormDataChange("customLlmName", e.target.value)
                }
                sx={{ mt: 0 }}
              />
            </>
          )}

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5 }}>
            Keys
          </Typography>
          <TextField
            fullWidth
            placeholder="Key"
            value={llmFormData.keys}
            onChange={(e) => handleFormDataChange("keys", e.target.value)}
            sx={{ mt: 0 }}
          />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5 }}>
            Status
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={llmFormData.status}
            onChange={(e) => handleFormDataChange("status", e.target.value)}
            sx={{ mt: 0 }}
          >
            <MenuItem value="" disabled>
              Select Status
            </MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </Select>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5 }}>
            Purchase Date
          </Typography>
          <TextField
            fullWidth
            type="date"
            placeholder="purchase date"
            value={llmFormData.purchaseDate}
            onChange={(e) =>
              handleFormDataChange("purchaseDate", e.target.value)
            }
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 0 }}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", p: "16px 24px" }}>
          <Button
            onClick={handleAddLLMClose}
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              border: "1px solid #1570EF",
              color: "#1570EF",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLLMSubmit}
            variant="contained"
            disabled={addLoading}
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              background: "#1570EF",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": {
                background: "#1570EF",
              },
            }}
          >
            {addLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Add LLM API & Credentials"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isViewModalOpen}
        sx={{
          "& .MuiDialog-paper": {
            width: "450px",
            maxWidth: "450px",
            borderRadius: "12px",
          },
        }}
        onClose={handleViewClose}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            fontSize: "18px",
            pb: 1,
            borderBottom: "1px solid #E5E5E5",
          }}
        >
          View Details
          <IconButton
            onClick={handleViewClose}
            size="small"
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 3 }}>
          {selectedLlmData && (
            <>
              <Box
                sx={{
                  display: "flex",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ minWidth: "120px", fontWeight: 400 }}
                >
                  LLM Name:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedLlmData.name}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ minWidth: "120px", fontWeight: 400 }}
                >
                  Key Name:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedLlmData.key_name || "N/A"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ minWidth: "120px", fontWeight: 400 }}
                >
                  Keys:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedLlmData.api_key}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ minWidth: "120px", fontWeight: 400 }}
                >
                  Status:
                </Typography>
                <Chip
                  label={selectedLlmData.is_active ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    bgcolor: selectedLlmData.is_active ? "#E3FFEC" : "#FEE2E2",
                    color: selectedLlmData.is_active ? "#5F9A72" : "#C33D1B",
                    borderRadius: "6px",
                    height: "22px",
                    fontWeight: 400,
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ minWidth: "120px", fontWeight: 400 }}
                >
                  Purchase Date:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedLlmData.purchase_date}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isEditLLMModalOpen}
        onClose={handleEditLLMClose}
        sx={{
          "& .MuiDialog-paper": {
            width: "400px",
            maxWidth: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "600", fontSize: "24px" }}
        >
          Edit LLM API & Credentials
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            LLM Name
          </Typography>
          <TextField
            fullWidth
            placeholder="LLM Name"
            value={editFormData.llmName}
            onChange={(e) =>
              handleEditFormDataChange("llmName", e.target.value)
            }
            sx={{ mt: 0 }}
          />
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Keys
          </Typography>
          <TextField
            fullWidth
            placeholder="Keys"
            value={editFormData.keys}
            onChange={(e) => handleEditFormDataChange("keys", e.target.value)}
            sx={{ mt: 0 }}
          />
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Status
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={editFormData.status}
            onChange={(e) => handleEditFormDataChange("status", e.target.value)}
            sx={{ mt: 0 }}
          >
            <MenuItem value="" disabled>
              Select Status
            </MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </Select>
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Purchase Date
          </Typography>
          <TextField
            fullWidth
            type="date"
            placeholder="purchase date"
            value={editFormData.purchaseDate}
            onChange={(e) =>
              handleEditFormDataChange("purchaseDate", e.target.value)
            }
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 0 }}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", p: "16px 24px" }}>
          <Button
            onClick={handleEditLLMClose}
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              border: "1px solid #1570EF",
              color: "#1570EF",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateLLM}
            variant="contained"
            disabled={editLoading}
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              background: "#1570EF",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": {
                background: "#1570EF",
              },
            }}
          >
            {editLoading && <CircularProgress size={24} color="inherit" />}
            Edit LLM API & Credentials
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteModalOpen}
        onClose={handleDeleteClose}
        sx={{
          "& .MuiDialog-paper": {
            width: "450px",
            maxWidth: "450px",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontSize: "24px",
            fontWeight: "600",
            mt: 1.5,
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", my: 2, mb: 0 }}>
          <Typography
            sx={{ fontWeight: 400, fontSize: "16px", color: "#333333" }}
          >
            Are you sure you want to delete this LLM API & Credentials?
          </Typography>
          <Typography
            variant="body2"
            color="#333333"
            sx={{ fontSize: "16px", mt: 1 }}
          >
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: "16px 24px" }}>
          <Button
            onClick={handleDeleteClose}
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              border: "1px solid #1570EF",
              color: "#1570EF",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteLLM}
            variant="contained"
            disabled={deleteLoading}
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              background: "#1570EF",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": {
                background: "#1570EF",
              },
            }}
          >
            {deleteLoading && <CircularProgress size={24} color="inherit" />}
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        sx={{
          "& .MuiDialog-paper": {
            width: "400px",
            maxWidth: "400px",
            borderRadius: "12px",
            textAlign: "center",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "600",
            fontSize: "24px",
            mt: 2,
          }}
        >
          Contact Developer
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Typography
            sx={{
              fontSize: "16px",
              color: "#333333",
              mb: 2,
            }}
          >
            Please contact to developer Team
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={handleSuccessModalClose}
            variant="contained"
            sx={{
              textTransform: "capitalize",
              padding: "10px 30px",
              background: "#1570EF",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": {
                background: "#1570EF",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiScraperSettings;
